/** Build Customers list URL preserving Support email search context. */
export function buildCustomersListPath(email?: string): string {
  const trimmed = email?.trim();
  return trimmed ? `/customers?email=${encodeURIComponent(trimmed)}` : '/customers';
}

/** Read email search query param from the current location. */
export function getCustomerEmailSearchParam(search: string): string | undefined {
  const value = new URLSearchParams(search).get('email');
  return value?.trim() || undefined;
}

/** Resolve email search from router state + query string. */
export function resolveCustomerEmailSearch(
  search: string,
  stateEmail?: string,
): string | undefined {
  return getCustomerEmailSearchParam(search) || stateEmail?.trim() || undefined;
}

/** Label for navigating back from customer detail to the customers list. */
export function getCustomersListBackLabel(email?: string): string {
  return email?.trim() ? 'Back to Customer' : 'Back to Customers';
}

export interface CustomerReturnNavigation {
  path: string;
  label: string;
}

export interface CustomerLocationState {
  returnTo?: CustomerReturnNavigation;
  highlightGroupId?: number;
}

export interface CustomerGroupListReturnOptions {
  highlightGroupId?: number;
  page?: number;
  search?: string;
  limit?: number;
}

/** Router state passed when opening customer group detail from the list. */
export interface CustomerGroupDetailLocationState {
  listPage?: number;
  listSearch?: string;
  listLimit?: number;
}

export interface CustomerGroupListUrlState {
  page: number;
  searchTerm: string;
  limit: number;
}

const DEFAULT_CUSTOMER_GROUP_LIST_LIMIT = 10;

/** Valid BC customer group ids are non-negative integers (including 0). */
export function isValidCustomerGroupId(groupId: number): boolean {
  return Number.isInteger(groupId) && groupId >= 0;
}

function parseCustomerGroupHighlightId(value: string | null | undefined): number | undefined {
  if (!value) {
    return undefined;
  }

  const parsed = parseInt(value, 10);
  return isValidCustomerGroupId(parsed) ? parsed : undefined;
}

/** Sync list page/search/limit from URL query params (including clearing absent keys). */
export function parseCustomerGroupListSearchParams(
  searchParams: URLSearchParams | string,
): CustomerGroupListUrlState {
  const params =
    typeof searchParams === 'string' ? new URLSearchParams(searchParams) : searchParams;

  const pageParam = params.get('page');
  let page = 1;
  if (pageParam) {
    const parsedPage = parseInt(pageParam, 10);
    if (!Number.isNaN(parsedPage) && parsedPage >= 1) {
      page = parsedPage;
    }
  }

  const limitParam = params.get('limit');
  let limit = DEFAULT_CUSTOMER_GROUP_LIST_LIMIT;
  if (limitParam) {
    const parsedLimit = parseInt(limitParam, 10);
    if (!Number.isNaN(parsedLimit) && parsedLimit >= 1) {
      limit = parsedLimit;
    }
  }

  return {
    page,
    searchTerm: params.get('name') ?? '',
    limit,
  };
}

/** Build URLSearchParams for the customer groups list from current UI state. */
export function buildCustomerGroupListSearchParams(
  options: {
    page: number;
    searchTerm?: string;
    limit?: number;
  },
  preserveParams?: URLSearchParams | string,
): URLSearchParams {
  const params = new URLSearchParams();
  const trimmedSearch = options.searchTerm?.trim();
  const limit = options.limit ?? DEFAULT_CUSTOMER_GROUP_LIST_LIMIT;

  if (options.page > 1) {
    params.set('page', String(options.page));
  }
  if (trimmedSearch) {
    params.set('name', trimmedSearch);
  }
  if (limit !== DEFAULT_CUSTOMER_GROUP_LIST_LIMIT) {
    params.set('limit', String(limit));
  }

  if (preserveParams) {
    const existing =
      typeof preserveParams === 'string'
        ? new URLSearchParams(preserveParams)
        : preserveParams;
    const groupId = existing.get('groupId');
    const parsedGroupId = parseCustomerGroupHighlightId(groupId);
    if (parsedGroupId !== undefined) {
      params.set('groupId', String(parsedGroupId));
    }
  }

  return params;
}

/** True when URL list params match the current page, debounced search, and limit. */
export function isCustomerGroupListUrlStateSynced(
  searchParams: URLSearchParams | string,
  state: CustomerGroupListUrlState,
): boolean {
  const urlState = parseCustomerGroupListSearchParams(searchParams);
  return (
    urlState.page === state.page &&
    urlState.searchTerm.trim() === state.searchTerm.trim() &&
    urlState.limit === state.limit
  );
}

/** Path + state for returning to the customer groups list with highlight. */
export function buildCustomerGroupListNavigation(
  options: CustomerGroupListReturnOptions = {},
): { path: string; state: CustomerLocationState } {
  return {
    path: customerGroupListReturn(options).path,
    state: {
      highlightGroupId:
        options.highlightGroupId !== undefined &&
        isValidCustomerGroupId(options.highlightGroupId)
          ? options.highlightGroupId
          : undefined,
    },
  };
}

/** Back target when customer detail was opened from the Customer Groups list. */
export function customerGroupListReturn(
  options: CustomerGroupListReturnOptions = {},
): CustomerReturnNavigation {
  const params = buildCustomerGroupListSearchParams({
    page: options.page ?? 1,
    searchTerm: options.search,
    limit: options.limit,
  });
  if (options.highlightGroupId !== undefined && isValidCustomerGroupId(options.highlightGroupId)) {
    params.set('groupId', String(options.highlightGroupId));
  }

  const query = params.toString();
  return {
    path: query ? `/customer-groups?${query}` : '/customer-groups',
    label: 'Back to Customer Groups',
  };
}

export function buildCustomerLocationState(
  returnTo: CustomerReturnNavigation,
  extras?: Pick<CustomerLocationState, 'highlightGroupId'>,
): CustomerLocationState {
  return { returnTo, ...extras };
}

/** Back target for customer detail — defaults to the customers list. */
export function resolveCustomerDetailBack(
  state: unknown,
  emailSearch?: string,
): CustomerReturnNavigation {
  const locationState = state as CustomerLocationState | null | undefined;
  if (locationState?.returnTo?.path && locationState.returnTo.label) {
    return locationState.returnTo;
  }

  return {
    path: buildCustomersListPath(emailSearch),
    label: getCustomersListBackLabel(emailSearch),
  };
}

/** Read highlighted customer group id from query string or router state. */
export function getCustomerGroupHighlightId(search: string, state?: unknown): number | undefined {
  const fromQuery = parseCustomerGroupHighlightId(new URLSearchParams(search).get('groupId'));
  if (fromQuery !== undefined) {
    return fromQuery;
  }

  const locationState = state as CustomerLocationState | null | undefined;
  const fromState = locationState?.highlightGroupId;
  return fromState !== undefined && isValidCustomerGroupId(fromState) ? fromState : undefined;
}

/** Customer detail path including email query for back-navigation context. */
export function buildCustomerDetailPath(customerId: number, email?: string): string {
  const trimmed = email?.trim();
  return trimmed
    ? `/customers/${customerId}?email=${encodeURIComponent(trimmed)}`
    : `/customers/${customerId}`;
}

export type CustomerOrderPreview = {
  id: number;
  status?: string;
  total_inc_tax?: string;
  date_created?: string;
};

export function getCustomerOrderCount(customer: Record<string, unknown>): number {
  if (typeof customer.orders_count === 'number') {
    return customer.orders_count;
  }
  if (Array.isArray(customer.orders)) {
    return customer.orders.length;
  }
  return 0;
}

/** True when the server (or a prior fetch) already attached order metadata. */
export function isCustomerOrdersEnriched(customer: Record<string, unknown>): boolean {
  return typeof customer.orders_count === 'number' && Array.isArray(customer.orders);
}

export function getCustomerOrderPreview(customer: Record<string, unknown>): CustomerOrderPreview[] {
  if (!Array.isArray(customer.orders)) {
    return [];
  }
  return customer.orders as CustomerOrderPreview[];
}

/** True when enriched rows cover the same customer IDs as the current query result. */
export function isCustomerListEnrichmentInSync(
  customers: Record<string, unknown>[],
  enrichedCustomers: Record<string, unknown>[],
): boolean {
  if (customers.length !== enrichedCustomers.length) {
    return false;
  }

  const enrichedIds = new Set(
    enrichedCustomers
      .map((customer) => customer.id)
      .filter((id): id is number => typeof id === 'number'),
  );

  return customers.every(
    (customer) => typeof customer.id === 'number' && enrichedIds.has(customer.id),
  );
}
